import { Button, Container, Group, Modal, MultiSelect, Table, TextInput } from "@mantine/core";
import React, { useEffect, useState } from "react";
import { Scope, ScopeType } from "../../types/managementTypes";
import {
  addScopes,
  getOrgsWithScope,
  getScopes,
  updateScopeByName,
} from "@utils/api-communication/RestApiCli";

import DistributorOrganizationsDrawer from "../common/DistributorOrganizationsDrawer";
import { SCOPE_TYPE } from "@model";
import { uniq } from "lodash";
import { useSelector } from "react-redux";
import { selectUserOrgId } from "@utils/redux-utils/selectors/userSelectors";
import { updateScopeList } from "@utils/CommonUtil";
// All available scope types
const scopeTypeOptions = uniq(Object.values(SCOPE_TYPE)).map((type) => ({
  value: type,
  label: type,
}));

const ScopeManagement: React.FC = () => {
  const orgId = useSelector(selectUserOrgId);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [newScope, setNewScope] = useState<Scope>({
    name: "",
    description: "",
    createdAt: new Date(),
    list: [],
  });
  const [editingScope, setEditingScope] = useState<Scope | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedScope, setSelectedScope] = useState<Scope | null>(null);
  const [showOrgsModal, setShowOrgsModal] = useState(false);
  const [orgsWithScope, setOrgsWithScope] = useState<string[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  useEffect(() => {
    getScopes(
      (response) => setScopes(response.data),
      (error) => console.error("Failed to fetch scopes", error),
    );
  }, []);

  const handleAddScope = () => {
    addScopes(
      newScope,
      (response) => {
        setScopes([...scopes, response.data]);
        setModalOpen(false);
        setNewScope({ name: "", description: "", createdAt: new Date(), list: [] });
      },
      (error) => console.error("Failed to add scope", error),
    );
  };

  const handleUpdateScope = () => {
    if (editingScope) {
      updateScopeByName(
        editingScope.name,
        newScope,
        () => {
          setScopes(
            scopes.map((scope) =>
              scope.name === editingScope.name ? { ...editingScope, ...newScope } : scope,
            ),
          );
          setModalOpen(false);
          setEditingScope(null);
          setNewScope({ name: "", description: "", createdAt: new Date(), list: [] });
          if (orgId) {
            updateScopeList({ orgId });
          }
        },
        (error) => console.error("Failed to update scope", error),
      );
    }
  };

  const openEditModal = (scope: Scope) => {
    setEditingScope(scope);
    setNewScope(scope);
    setModalOpen(true);
  };

  const handleShowOrgs = (scopeName: string) => {
    setLoadingOrgs(true);
    setShowOrgsModal(true);
    setOrgsWithScope([]);
    getOrgsWithScope(
      scopeName,
      (res) => {
        setOrgsWithScope(res.data || []);
        setLoadingOrgs(false);
      },
      (_err) => {
        setOrgsWithScope([]);
        setLoadingOrgs(false);
      },
    );
  };

  return (
    <Container>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1>Scope Management</h1>
        <Button
          onClick={() => {
            setModalOpen(true);
            setEditingScope(null);
            setNewScope({ name: "", description: "", createdAt: new Date(), list: [] });
          }}
        >
          Add Scope
        </Button>
      </div>
      <Table highlightOnHover withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Created At</Table.Th>
            <Table.Th>Actions</Table.Th>
            <Table.Th>Show</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {scopes.map((scope) => (
            <Table.Tr key={scope.name}>
              <Table.Td>{scope.name}</Table.Td>
              <Table.Td>{scope.description}</Table.Td>
              <Table.Td>{new Date(scope.createdAt).toLocaleString()}</Table.Td>
              <Table.Td>
                <Button size="xs" onClick={() => openEditModal(scope)}>
                  Edit
                </Button>
                <Button
                  size="xs"
                  ml="xs"
                  color="teal"
                  onClick={() => {
                    setSelectedScope(scope);
                    setDrawerOpen(true);
                  }}
                >
                  Assign
                </Button>
              </Table.Td>
              <Table.Td>
                <Button size="xs" variant="outline" onClick={() => handleShowOrgs(scope.name)}>
                  Show
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingScope ? "Edit Scope" : "Add Scope"}
      >
        <TextInput
          label="Name"
          value={newScope.name}
          onChange={(e) => setNewScope({ ...newScope, name: e.target.value })}
        />
        <TextInput
          label="Description"
          value={newScope.description}
          onChange={(e) => setNewScope({ ...newScope, description: e.target.value })}
        />
        <MultiSelect
          label="Scope Types"
          data={scopeTypeOptions}
          value={newScope.list as string[]}
          onChange={(value) => setNewScope({ ...newScope, list: value as ScopeType[] })}
        />
        <Group justify={"flex-start"} mt="md">
          <Button onClick={editingScope ? handleUpdateScope : handleAddScope}>
            {editingScope ? "Update" : "Add"}
          </Button>
        </Group>
      </Modal>

      <DistributorOrganizationsDrawer
        opened={drawerOpen && selectedScope !== null}
        onClose={() => setDrawerOpen(false)}
        scopeName={selectedScope?.name}
      />

      <Modal
        opened={showOrgsModal}
        onClose={() => setShowOrgsModal(false)}
        title="Organizations with this Scope"
      >
        {loadingOrgs ? (
          <div>Loading...</div>
        ) : (
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {orgsWithScope.length === 0 ? (
              <div>No organizations found.</div>
            ) : (
              <ul>
                {orgsWithScope.map((orgId) => (
                  <li key={orgId}>{orgId}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Modal>
    </Container>
  );
};

export default ScopeManagement;
